// src/App.js
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { getTheme } from "./theme";
import { installNetworkErrorMessages } from "./services/networkErrors";
import { ThemeProvider, CssBaseline } from "@mui/material";
import TruncatedTextTooltip from "./components/TruncatedTextTooltip";
import SceneCalculationOverlay from "./components/pv/SceneCalculationOverlay";
import { UiProvider, useUi } from "./context/UiContext";
import AppLayout from "./layouts/AppLayout";
import Dashboard from "./pages/Dashboard";
import AdminUsers from "./pages/AdminUsers";
import Optimize from "./pages/Optimize";
import Login from "./pages/Login";
import FeasibilityTechnical from "./pages/feasibility/FeasibilityTechnical";
import FeasibilityEconomics from "./pages/feasibility/FeasibilityEconomics";
import FeasibilityGeneral from "./pages/feasibility/FeasibilityGeneral";
import FeasibilityWeather from "./pages/feasibility/FeasibilityWeather";
import FeasibilitySimulation from "./pages/feasibility/FeasibilitySimulation";
import OpenSourceNotices from "./pages/OpenSourceNotices";
import MarketOpsShell from "./marketOps/MarketOpsShell";
import OperationsDesk from "./marketOps/pages/OperationsDesk";
import Assets from "./marketOps/pages/Assets";
import ProjectOverview from "./pages/project/ProjectOverview";
import AssetSetup from "./pages/project/AssetSetup";
import AssetScopeBar from "./components/AssetScopeBar";
import FeasibilityLayout from "./layouts/FeasibilityLayout";
import OperationsLayout from "./layouts/OperationsLayout";
import DashboardAccessGate from "./routes/DashboardAccessGate";
import Positions from "./marketOps/pages/Positions";
import Passes from "./marketOps/pages/Passes";
import Orders from "./marketOps/pages/Orders";
import Reserves from "./marketOps/pages/Reserves";
import Settlement from "./marketOps/pages/Settlement";
import Inputs from "./marketOps/pages/Inputs";
import ParticipationSettings from "./marketOps/pages/Settings";
import MonitoringShell from "./monitoring/MonitoringShell";
import MonitoringPortfolio from "./monitoring/pages/Portfolio";
import MonitoringPark from "./monitoring/pages/Park";
import MonitoringAlarms from "./monitoring/pages/Alarms";
import MonitoringReports from "./monitoring/pages/Reports";
import MonitoringDataSources from "./monitoring/pages/DataSources";
import AdminRoute from "./routes/AdminRoute";
import ProtectedRoute from "./routes/ProtectedRoute";
import StagingAdminGate from "./routes/StagingAdminGate";
import { FileProvider } from "./context/FileContext";
import { AuthProvider } from "./context/AuthContext";
import { EngineProvider } from "./context/EngineContext";
import { HistoryProvider } from "./context/HistoryContext";
import { DemoProvider } from "./context/DemoContext";
import FeasibilityPvSizing from "./pages/feasibility/FeasibilityPvSizing";
import { LanguageProvider } from "./context/LanguageContext";

// Once, before anything renders: every fetch in the app then reports a lost
// connection in one plain sentence instead of the browser's "Failed to fetch".
installNetworkErrorMessages();

function AppWithTheme({ children }) {
  const { darkMode } = useUi();

  const theme = React.useMemo(
    () => getTheme(darkMode),
    [darkMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* The whole text of a label cut short with "…", on hover. */}
      <TruncatedTextTooltip />
      {/* The application's lock while the layout's shading is calculated. */}
      <SceneCalculationOverlay />
      {children}
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <EngineProvider>
          <HistoryProvider>
            <DemoProvider>
              <FileProvider>
                <UiProvider>
                  <AppWithTheme>
                    <BrowserRouter>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route element={<ProtectedRoute />}>
                        <Route element={<StagingAdminGate />}>
                        <Route element={<AppLayout />}>
                          <Route index element={<Navigate to="/project/overview" replace />} />
                          <Route path="/project/overview" element={<ProjectOverview />} />
                          {/* One asset's specification as tabs; the former per-page
                              routes redirect into the matching tab. */}
                          <Route path="/project/asset" element={<AssetSetup />} />
                          <Route path="/Project" element={<Navigate to="/project/asset?tab=general" replace />} />
                          <Route path="/Photovoltaic" element={<Navigate to="/project/asset?tab=pv" replace />} />
                          <Route path="/Wind" element={<Navigate to="/project/asset?tab=wind" replace />} />
                          <Route path="/BESS" element={<Navigate to="/project/asset?tab=bess" replace />} />
                          <Route path="/Grid" element={<Navigate to="/project/asset?tab=grid" replace />} />
                          {/* Operations: one section, two-level navigation inside it. */}
                          <Route element={<OperationsLayout />}>
                            <Route path="/optimize" element={<Optimize />} />
                          </Route>
                          {/* /dashboard hosts Data & Forecasts views (asset scope bar) and two
                              Operations views (Operations navigation); the wrapper follows the view. */}
                          <Route element={<AssetScopeBar opsAware />}>
                            <Route path="/dashboard" element={<DashboardAccessGate><Dashboard /></DashboardAccessGate>} />
                          </Route>
                          {/* Operations workspace — gate-driven multi-market views under
                              /ops/*. Daily Optimization (/optimize), Historic Runs and Bid
                              Schedules (/dashboard) are untouched and sit in the same workspace. */}
                          <Route element={<OperationsLayout />}>
                          <Route path="/ops" element={<MarketOpsShell />}>
                            <Route index element={<Navigate to="/ops/desk" replace />} />
                            <Route path="assets" element={<Assets />} />
                            <Route path="desk" element={<OperationsDesk />} />
                            <Route path="positions" element={<Positions />} />
                            <Route path="passes" element={<Passes />} />
                            <Route path="orders" element={<Orders />} />
                            <Route path="reserves" element={<Reserves />} />
                            <Route path="settlement" element={<Settlement />} />
                            <Route path="inputs" element={<Inputs />} />
                            <Route path="settings" element={<ParticipationSettings />} />
                          </Route>
                          </Route>
                          <Route path="/market-ops/*" element={<Navigate to="/ops/desk" replace />} />
                          {/* Asset Monitoring: live PV park monitoring inside the Operations
                              workspace. Not behind OperationsLayout on purpose - a monitoring-only
                              client is not gated by market operations. */}
                          <Route path="/monitoring" element={<MonitoringShell />}>
                            <Route index element={<MonitoringPortfolio />} />
                            <Route path="parks/:sourceId" element={<MonitoringPark />} />
                            <Route path="alarms" element={<MonitoringAlarms />} />
                            <Route path="reports" element={<MonitoringReports />} />
                            <Route path="sources" element={<MonitoringDataSources />} />
                          </Route>
                          <Route
                            path="/admin/users"
                            element={
                              <AdminRoute>
                                <AdminUsers />
                              </AdminRoute>
                            }
                          />
                          <Route path="/feasibility" element={<Navigate to="/feasibility/general" replace />} />
                          <Route element={<FeasibilityLayout />}>
                            <Route path="/feasibility/general" element={<FeasibilityGeneral />} />
                            <Route path="/feasibility/weather" element={<FeasibilityWeather />} />
                            <Route path="/feasibility/technical" element={<FeasibilityTechnical />} />
                            <Route path="/feasibility/economics" element={<FeasibilityEconomics />} />
                            <Route path="/feasibility/simulation" element={<FeasibilitySimulation />} />
                          </Route>
                          {/* An analysis beside the workflow, not a step of it: no step
                              counter, no Back / Next. */}
                          <Route path="/feasibility/pv-sizing" element={<FeasibilityPvSizing />} />
                          <Route path="/open-source-notices" element={<OpenSourceNotices />} />
                          <Route path="*" element={<Navigate to="/Photovoltaic" replace />} />
                        </Route>
                        </Route>
                      </Route>
                    </Routes>
                    </BrowserRouter>
                  </AppWithTheme>
                </UiProvider>
              </FileProvider>
            </DemoProvider>
          </HistoryProvider>
        </EngineProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}
